import React from 'react';
import {
    Box,
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    CardActions,
    Button,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    useTheme,
    IconButton,
} from '@mui/material';
import { Check, Close, TrendingUp, ArrowBack, Brightness4, Brightness7 } from '@mui/icons-material';
import { Link } from '@tanstack/react-router';
import { useColorMode } from '../theme';
import { TIERS, formatPrice } from '../config/pricing';
import { useCurrency } from '../hooks/useCurrency';

export const PricingPage: React.FC = () => {
    const theme = useTheme();
    const { toggleColorMode, mode } = useColorMode();
    const [currency] = useCurrency();

    // Derived from the shared config rather than restated. These two pages
    // drifted to $5 and $6, and this page still advertised a "Pro" tier with an
    // options tracker that no longer exists. One source, no drift.
    const plans = TIERS.map((t) => ({
        title: t.name,
        price: formatPrice(t, currency),
        period: t.monthly.USD === 0 ? '' : '/ month',
        description: t.blurb,
        features: t.points.map((text) => ({ text, included: true })),
        buttonText: t.cta,
        buttonVariant: t.highlight ? ('contained' as const) : ('outlined' as const),
        highlight: !!t.highlight,
        disabled: false,
    }));

    return (
        <Box sx={{ bgcolor: 'transparent', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
            {/* Navbar */}
            <Box component="nav" sx={{ py: 3, px: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', maxWidth: 'lg', mx: 'auto', width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button component={Link} to="/" startIcon={<ArrowBack />} color="inherit">
                        Back
                    </Button>
                    <Typography variant="h5" fontWeight="bold" color="primary.main" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TrendingUp /> FolioWorth
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <IconButton onClick={toggleColorMode} color="inherit">
                        {mode === 'dark' ? <Brightness7 /> : <Brightness4 />}
                    </IconButton>
                    <Button component={Link} to="/login" variant="text" color="inherit" sx={{ fontWeight: 600 }}>
                        Sign In
                    </Button>
                    <Button component={Link} to="/register" variant="contained" color="primary" sx={{ borderRadius: 50, px: 3 }}>
                        Get Started
                    </Button>
                </Box>
            </Box>

            <Box sx={{ py: 8 }}>
                <Container maxWidth="lg">
                    <Box sx={{ textAlign: 'center', mb: 8 }}>
                        <Typography variant="overline" color="primary" fontWeight="bold" letterSpacing={1.5}>
                            PRICING
                        </Typography>
                        <Typography variant="h2" fontWeight="bold" gutterBottom sx={{ mt: 1 }}>
                            Choose the plan that fits your needs
                        </Typography>
                        <Typography variant="h5" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
                            Start for free and upgrade as your portfolio grows. No hidden fees.
                        </Typography>
                    </Box>

                    <Grid container spacing={4} alignItems="flex-start" justifyContent="center">
                        {plans.map((plan, index) => (
                            <Grid item xs={12} md={5} key={index}>
                                <Card
                                    elevation={plan.highlight ? 8 : 1}
                                    sx={{
                                        borderRadius: 4,
                                        position: 'relative',
                                        border: plan.highlight ? `2px solid ${theme.palette.primary.main}` : undefined,
                                        transition: 'transform 0.2s',
                                        '&:hover': {
                                            transform: 'translateY(-8px)',
                                        },
                                    }}
                                >
                                    <CardContent sx={{ p: 4 }}>
                                        <Typography variant="h4" fontWeight="bold" gutterBottom>
                                            {plan.title}
                                        </Typography>
                                        <Typography variant="body1" color="text.secondary" paragraph>
                                            {plan.description}
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 4 }}>
                                            <Typography variant="h2" fontWeight="bold">
                                                {plan.price}
                                            </Typography>
                                            <Typography variant="h6" color="text.secondary">
                                                {plan.period}
                                            </Typography>
                                        </Box>
                                        <List>
                                            {plan.features.map((feature, idx) => (
                                                <ListItem key={idx} disableGutters sx={{ py: 1 }}>
                                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                                        {feature.included ? (
                                                            <Check color="primary" fontSize="small" />
                                                        ) : (
                                                            <Close color="action" fontSize="small" />
                                                        )}
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={feature.text}
                                                        primaryTypographyProps={{
                                                            color: feature.included ? 'text.primary' : 'text.disabled',
                                                            fontWeight: feature.included ? 500 : 400,
                                                        }}
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </CardContent>
                                    <CardActions sx={{ p: 4, pt: 0 }}>
                                        <Button
                                            component={Link}
                                            to={plan.highlight ? '/register?plan=pro' : '/register'}
                                            fullWidth
                                            variant={plan.buttonVariant}
                                            size="large"
                                            disabled={plan.disabled}
                                            sx={{ py: 1.5 }}
                                        >
                                            {plan.buttonText}
                                        </Button>
                                    </CardActions>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>
        </Box>
    );
};
